import React, { useState } from 'react';
import { GitCommit } from '../hooks/useGit';

// Legacy commit type for backwards compatibility
export type Commit = {
  id: string;
  message: string;
  author: string;
  date: string;
  summary: string;
  diff: string[];
};

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  if (diffDay < 30) return `${Math.floor(diffDay / 7)}w ago`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

type CommitPanelProps = {
  commits: Commit[];
  selectedCommitId: string;
  onSelect: (id: string) => void;
  // New git commits from useGit
  gitCommits?: GitCommit[];
  isLoadingGitCommits?: boolean;
  onFetchGitCommits?: (count?: number) => Promise<GitCommit[]>;
};

export const CommitPanel = React.memo(function CommitPanel({
  commits,
  selectedCommitId,
  onSelect,
  gitCommits,
  isLoadingGitCommits,
  onFetchGitCommits,
}: CommitPanelProps) {
  const [filterAuthor, setFilterAuthor] = useState('');

  // Use gitCommits if available, otherwise fallback to legacy commits
  const useGitData = gitCommits && gitCommits.length > 0;

  // Filter gitCommits by author
  const filteredGitCommits = useGitData && filterAuthor
    ? gitCommits.filter(c =>
        c.author.toLowerCase().includes(filterAuthor.toLowerCase()) ||
        c.email.toLowerCase().includes(filterAuthor.toLowerCase())
      )
    : gitCommits || [];

  // Legacy commit handling
  const selectedCommit = commits.find((c) => c.id === selectedCommitId) || commits[0];
  const selectedGitCommit = useGitData
    ? filteredGitCommits.find((c) => c.shortHash === selectedCommitId) || filteredGitCommits[0]
    : null;

  return (
    <div className="console-card">
      <div className="panel-header">
        <div>
          <p className="eyebrow">Commits</p>
          <h2>History</h2>
        </div>
        {useGitData && (
          <input
            type="text"
            placeholder="Filter by author..."
            value={filterAuthor}
            onChange={(e) => setFilterAuthor(e.target.value)}
            className="git-log-filter"
            style={{ width: '150px' }}
          />
        )}
      </div>
      <div className="commit-panel">
        <div className="commit-list">
          {isLoadingGitCommits ? (
            <div className="git-log-loading">Loading commits...</div>
          ) : useGitData ? (
            filteredGitCommits.map((commit, index) => (
              <div
                key={commit.hash}
                className={`commit-row ${commit.shortHash === selectedGitCommit?.shortHash ? 'active' : ''}`}
                onClick={() => onSelect(commit.shortHash)}
              >
                <div className="commit-graph">
                  <div className={`commit-node ${index === 0 ? 'current' : ''}`} />
                  {index < filteredGitCommits.length - 1 && <div className="commit-line" />}
                </div>
                <div className="commit-meta">
                  <div className="commit-message">{commit.message}</div>
                  <div className="commit-sub">
                    <span className="commit-hash">{commit.shortHash}</span>
                    <span>{commit.author}</span>
                    <span>{formatRelativeTime(commit.date)}</span>
                  </div>
                </div>
              </div>
            ))
          ) : (
            commits.map((commit) => (
              <div
                key={commit.id}
                className={`commit-row ${commit.id === selectedCommit?.id ? 'active' : ''}`}
                onClick={() => onSelect(commit.id)}
              >
                <div className="commit-id">{commit.id}</div>
                <div className="commit-meta">
                  <div className="commit-message">{commit.message}</div>
                  <div className="commit-sub">
                    <span>{commit.author}</span> · <span>{commit.date}</span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        {!useGitData && selectedCommit && (
          <div className="commit-diff">
            <div className="diff-header">
              <span className="pill small outline">{selectedCommit.id}</span>
              <span className="pill small subtle">{selectedCommit.summary}</span>
            </div>
            <pre className="diff-body">
              {selectedCommit.diff.map((line) => (
                <div
                  key={line}
                  className={`diff-line ${line.startsWith('+') ? 'add' : line.startsWith('-') ? 'del' : ''}`}
                >
                  {line}
                </div>
              ))}
            </pre>
          </div>
        )}
        {useGitData && onFetchGitCommits && gitCommits.length >= 50 && (
          <button
            className="git-log-load-more"
            onClick={() => onFetchGitCommits(gitCommits.length + 50)}
            style={{ margin: '8px 12px' }}
          >
            Load more commits
          </button>
        )}
      </div>
    </div>
  );
});
